export interface FailedGeneration {
  // The persisted user message whose reply generation failed, so the UI can
  // highlight it and offer a resend. Null id falls back to "the last user
  // message" when matching in the view.
  messageId: string | null
  content: string
  agentId?: string | null
  /** LP-113: set when the failure was connector offline (toast + auto-resend). */
  reason?: 'connector_offline'
  /** Ensures one-shot auto-resend per offline failure. */
  autoResendAttempted?: boolean
}

// Failed generations are persisted so the "highlight + resend" affordance
// survives a page refresh — the failed turn is a dangling user message on the
// server with no failure flag, so the client must remember it locally.
const FAILED_GENERATION_STORAGE_KEY = 'laplace:failed-generation'

export function readFailedGenerations(): Record<string, FailedGeneration> {
  if (typeof window === 'undefined') {
    return {}
  }

  try {
    const raw = window.localStorage.getItem(FAILED_GENERATION_STORAGE_KEY)
    if (!raw) {
      return {}
    }

    const parsed = JSON.parse(raw) as unknown
    if (parsed === null || typeof parsed !== 'object') {
      return {}
    }

    return parsed as Record<string, FailedGeneration>
  } catch {
    return {}
  }
}

export function writeFailedGenerations(
  value: Record<string, FailedGeneration | undefined>,
) {
  if (typeof window === 'undefined') {
    return
  }

  try {
    if (Object.keys(value).length === 0) {
      window.localStorage.removeItem(FAILED_GENERATION_STORAGE_KEY)
    } else {
      window.localStorage.setItem(
        FAILED_GENERATION_STORAGE_KEY,
        JSON.stringify(value),
      )
    }
  } catch {
    // Ignore quota / serialization errors — persistence is best-effort.
  }
}
